import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container } from './Container';
import {
  useI18n,
  useLocalizedPath,
  SUPPORTED_LANGS,
  LANG_LABELS,
  type Lang,
} from '@/hooks/useI18n';
import { useTranslation } from '@/hooks/useTranslation';
import { useScrollTracking } from '@/hooks/useScrollTracking';
import { useTimeOnPage } from '@/hooks/useTimeOnPage';

function useNavItems() {
  const { t } = useTranslation();
  return [
    { to: '/', label: t('nav.guide') },
    { to: '/blog', label: t('nav.blog') },
    { to: '/o-que-fazer-ilha-do-mel', label: t('nav.thingsToDo') },
    { to: '/pousadas', label: t('nav.whereToStay') },
  ];
}

function LangSelector() {
  const { lang, setLang } = useI18n();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="px-2 py-1.5 rounded-lg font-body text-xs font-semibold uppercase tracking-wide bg-sand-50 text-warm-dark hover:bg-sand-100 border border-sand-200 transition-colors"
        aria-label={t('layout.langSelectorAria')}
      >
        {lang === 'pt-BR' ? 'PT' : lang}
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-32 bg-white rounded-xl border border-sand-200 shadow-lg overflow-hidden z-50">
          {SUPPORTED_LANGS.map(l => (
            <button
              key={l}
              onClick={() => {
                setLang(l as Lang);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 font-body text-sm hover:bg-sand-50 transition-colors ${
                l === lang ? 'text-teal-deep font-semibold bg-teal-light/5' : 'text-warm-dark'
              }`}
            >
              {LANG_LABELS[l as Lang]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  useScrollTracking();
  useTimeOnPage(10);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { lang } = useI18n();
  const localize = useLocalizedPath();
  const { t } = useTranslation();
  const navItems = useNavItems();

  return (
    <div className="min-h-screen bg-sand-100 text-warm-dark font-body">
      <Helmet defaultTitle="Vem Pra Ilha do Mel" titleTemplate="%s | Vem Pra Ilha do Mel">
        <html lang={lang === 'pt-BR' ? 'pt-BR' : lang} />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#0B7B7B" />
        <link
          rel="icon"
          type="image/svg+xml"
          href="data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><text y='.9em' font-size='90'>🏝️</text></svg>"
        />
      </Helmet>
      {/* Navbar */}
      <nav className="sticky top-0 z-40 bg-white shadow-[0_1px_8px_rgba(0,0,0,0.04)]" aria-label="Menu principal">
        <Container>
          <div className="flex items-center justify-between h-16">
            <Link to={localize('/')} className="flex items-center gap-2 flex-shrink-0">
              <span className="text-2xl">🏝️</span>
              <span className="font-display text-lg text-teal-deep tracking-wide hidden sm:block">
                VEMPRAILHADOMEL<span className="text-coral">.COM.BR</span>
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-1">
              {navItems.map(item => {
                const localizedTo = localize(item.to);
                const isActive =
                  location.pathname === localizedTo ||
                  (item.to !== '/' && location.pathname.startsWith(localizedTo));
                return (
                  <Link
                    key={item.to}
                    to={localizedTo}
                    className={`px-3 py-2 rounded-lg font-body text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive
                        ? 'bg-teal-light/10 text-teal-deep'
                        : 'text-warm-gray hover:text-teal-deep hover:bg-sand-50'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
              <div className="ml-2">
                <LangSelector />
              </div>
            </div>

            <button
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? t('layout.closeMenu') : t('layout.openMenu')}
              className="md:hidden p-2 rounded-lg text-warm-gray hover:text-teal-deep hover:bg-sand-50"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {menuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>

          {menuOpen && (
            <div className="md:hidden pb-4 border-t border-sand-200 pt-2">
              {navItems.map(item => {
                const localizedTo = localize(item.to);
                const isActive = location.pathname === localizedTo;
                return (
                  <Link
                    key={item.to}
                    to={localizedTo}
                    onClick={() => setMenuOpen(false)}
                    className={`block px-3 py-2.5 rounded-lg font-body text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-teal-light/10 text-teal-deep'
                        : 'text-warm-gray hover:text-teal-deep hover:bg-sand-50'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
              <div className="px-3 pt-3 mt-2 border-t border-sand-200">
                <LangSelector />
              </div>
            </div>
          )}
        </Container>
      </nav>

      <main>{children}</main>

      {/* Footer */}
      <footer className="bg-warm-dark mt-16">
        <Container className="py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">🏝️</span>
                <span className="font-display text-lg text-sand-100 tracking-wide">
                  VEMPRAILHADOMEL.COM.BR
                </span>
              </div>
              <p className="font-body text-sm text-sand-300 leading-relaxed">
                {t('footer.tagline')}
              </p>
            </div>
            <div>
              <p className="font-display text-sand-100 mb-3 tracking-wide text-base">{t('footer.know')}</p>
              <ul className="space-y-2 font-body text-sm text-sand-300">
                <li>
                  <Link
                    to={localize('/')}
                    className="hover:text-teal-light transition-colors"
                  >
                    {t('footer.guide')}
                  </Link>
                </li>
                <li>
                  <Link
                    to={localize('/o-que-fazer-ilha-do-mel')}
                    className="hover:text-teal-light transition-colors"
                  >
                    {t('footer.thingsToDo')}
                  </Link>
                </li>
                <li>
                  <Link
                    to={localize('/restaurantes-ilha-do-mel')}
                    className="hover:text-teal-light transition-colors"
                  >
                    {t('footer.bestRestaurants')}
                  </Link>
                </li>
                <li>
                  <Link
                    to={localize('/bares-ilha-do-mel')}
                    className="hover:text-teal-light transition-colors"
                  >
                    {t('footer.bestBars')}
                  </Link>
                </li>
                <li>
                  <Link
                    to={localize('/pousadas-ilha-do-mel')}
                    className="hover:text-teal-light transition-colors"
                  >
                    {t('footer.bestInns')}
                  </Link>
                </li>
                <li>
                  <Link
                    to={localize('/praias-ilha-do-mel')}
                    className="hover:text-teal-light transition-colors"
                  >
                    {t('footer.bestBeaches')}
                  </Link>
                </li>
                <li>
                  <Link
                    to={localize('/pontos-turisticos-ilha-do-mel')}
                    className="hover:text-teal-light transition-colors"
                  >
                    {t('footer.bestAttractions')}
                  </Link>
                </li>
                <li>
                  <Link
                    to={localize('/trilhas-ilha-do-mel')}
                    className="hover:text-teal-light transition-colors"
                  >
                    {t('footer.bestTrails')}
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <p className="font-display text-sand-100 mb-3 tracking-wide text-base">
                {t('footer.entities')}
              </p>
              <ul className="space-y-2 font-body text-sm text-sand-300">
                <li>
                  <Link
                    to={localize('/praias')}
                    className="hover:text-teal-light transition-colors"
                  >
                    {t('footer.beaches')}
                  </Link>
                </li>
                <li>
                  <Link
                    to={localize('/morros')}
                    className="hover:text-teal-light transition-colors"
                  >
                    {t('footer.hills')}
                  </Link>
                </li>
                <li>
                  <Link to={localize('/vilas')} className="hover:text-teal-light transition-colors">
                    {t('footer.villages')}
                  </Link>
                </li>
                <li>
                  <Link to={localize('/ilhas')} className="hover:text-teal-light transition-colors">
                    {t('footer.neighboringIslands')}
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <p className="font-display text-sand-100 mb-3 tracking-wide text-base">
                {t('footer.partners')}
              </p>
              <ul className="space-y-2 font-body text-sm text-sand-300">
                <li>
                  <Link
                    to={localize('/experiencias')}
                    className="hover:text-teal-light transition-colors"
                  >
                    Passeio 360° — Carol
                  </Link>
                </li>
                <li>
                  <Link
                    to={localize('/experiencias')}
                    className="hover:text-teal-light transition-colors"
                  >
                    Batismo de Vela — Luiz
                  </Link>
                </li>
                <li>
                  <Link
                    to={localize('/pousadas')}
                    className="hover:text-teal-light transition-colors"
                  >
                    Pousada Canto da Galheta
                  </Link>
                </li>
                <li>
                  <Link
                    to={localize('/pousadas')}
                    className="hover:text-teal-light transition-colors"
                  >
                    Pousada Fim Da Trilha
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-sand-400/20 mt-8 pt-8 text-center font-body text-xs text-sand-300">
            <p>{t('footer.copyright')}</p>
          </div>
        </Container>
      </footer>
    </div>
  );
}
